import { useEffect } from 'react'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import { isCommunityImagesObjectPath } from 'Pods/ThreeDStudio/constants'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import { parseRunSlugFromPathname } from './parseRunSlugFromPathname'
import { setCanonicalUrl } from './setCanonicalUrl'
import { setMetaDescription } from './setMetaDescription'
import useTranslation from './useTranslation'

// Stored titles read "<Name> 3D Model", so strip the trailing "3D Model" before
// the seo template re-appends "3D model" — mirrors
// ThreeDStudioController#output_model_name.
const stripModelSuffix = (name) => {
  const trimmed = name.trim()
  return trimmed.replace(/\s*3d\s*model\s*$/i, '').trim() || trimmed
}

// Keeps document.title, the meta description and the canonical URL in sync on client-side
// navigation. The server renders them per URL (ThreeDStudioController#studio_meta_title,
// #studio_meta_description and _hreflangs.slim), but switching pages inside the SPA only
// pushes history state, so without this they go stale until a full reload.
const usePageTitleSync = () => {
  const t = useTranslation()
  const location = useLocation()
  const pathname = location?.pathname ?? ''
  const modelName = useThreeDStudioStore(
    state => state.ui?.openedModel?.title ?? state.ui?.openedModel?.prompt ?? ''
  )

  useEffect(() => {
    if (modelName) {
      const name = stripModelSuffix(modelName)
      document.title = t('seo.outputTitle', { name })
      setMetaDescription(t('seo.outputDescription', { name }))
      setCanonicalUrl()
      return
    }

    // A run URL whose model has not loaded yet: keep the current (server
    // rendered) meta instead of flashing the index one mid-fetch.
    if (parseRunSlugFromPathname(pathname) != null) {
      return
    }

    // A community image URL: the server renders the image's meta, and the gallery
    // modal keeps it in step as the user moves between images.
    if (/^\/ai-image-generator\/images\/[^/]+/.test(pathname)) {
      return
    }

    // The per-object views name their object in the title, which only the server knows
    // (ThreeDStudioController#studio_meta_title). Without this the page hydrates and replaces
    // its own SEO title with the index one.
    if (isCommunityImagesObjectPath(pathname)) {
      return
    }

    const isGallery = pathname === '/ai-3d-model-generator/3d-models'
      || pathname === '/ai-3d-model-generator/3d-models/'
    const isImageGallery = pathname === '/ai-image-generator/images'
      || pathname === '/ai-image-generator/images/'
    if (isImageGallery) {
      document.title = t('seo.imagesTitle')
      setMetaDescription(t('seo.imagesDescription'))
      setCanonicalUrl()
      return
    }
    document.title = t(isGallery ? 'seo.galleryTitle' : 'seo.indexTitle')
    setMetaDescription(t(isGallery ? 'seo.galleryDescription' : 'seo.indexDescription'))
    setCanonicalUrl()
  }, [modelName, pathname, t])
}

export default usePageTitleSync
